import { oc } from "@orpc/contract";
import { JSON_SCHEMA_OUTPUT_REGISTRY } from "@orpc/zod/zod4";
import { z } from "zod";
import {
  AgentChannelSchema,
  ApprovalIssueSchema,
  FieldProvenanceSchema,
} from "@erp/shared-types";
import { DiscountValidationResultSchema } from "./sales-order-drafts.contract";

/**
 * Relational draft-sales-order editing contracts (workflow-native approval flow).
 *
 * Reads + per-line/header mutations over `ar_draft_sales_orders` +
 * `ar_draft_sales_order_line_items`. Line mutations are keyed by the stable
 * line-item `id` (not array index), the relational replacement for the staged
 * flow's PATCH-whole-JSONB pattern.
 */

const UUIDSchema = z.string().uuid();

// DECIMAL columns come back from Sequelize as strings; accept either on the wire.
const NumericSchema = z.union([z.string(), z.number()]);

const DraftLineItemSchema = z
  .object({
    id: UUIDSchema,
    lineNumber: z.number().int(),
    customerIdentifier: z.string().nullable().optional(),
    customerDescription: z.string().nullable().optional(),
    quantity: NumericSchema.nullable().optional(),
    unitPrice: NumericSchema.nullable().optional(),
    amount: NumericSchema.nullable().optional(),
    unit: z.string().nullable().optional(),
    warehouse: z.string().nullable().optional(),
    selectedProductVariantId: UUIDSchema.nullable().optional(),
    selectedCustomerProductId: UUIDSchema.nullable().optional(),
    inventoryStatus: z.string().nullable().optional(),
    // Stamped facts the approval conditions read.
    listPrice: NumericSchema.nullable().optional(),
    listPriceMismatch: z.boolean().nullable().optional(),
    approvalIssue: ApprovalIssueSchema.nullable().optional(),
    customerSpecificDiscountVerification: z.unknown().nullable().optional(),
    // The sparse long tail (sku, itemName, origin, charges, EDI loops, …).
    additionalData: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  // No passthrough: Zod strips the model's extra columns at the wire boundary,
  // and the trimmed type lets the handler return `row.toJSON()` via width subtyping.
  .strip();

const RevisionCarryForwardPreviewSchema = z.object({
  predecessorDraftId: UUIDSchema,
  predecessorRevisionNumber: z.number().int(),
  plan: z.object({
    headerOverrides: z.array(
      z.object({ field: z.string(), value: z.unknown() }),
    ),
    linePrices: z.array(
      z.object({
        revisionLineId: UUIDSchema,
        customerIdentifier: z.string().nullable(),
        from: z.string(),
        to: z.string(),
        editedBy: z.string().nullable(),
      }),
    ),
    skippedLines: z.array(
      z.object({
        customerIdentifier: z.string().nullable(),
        reason: z.string(),
      }),
    ),
    approvals: z.array(
      z.object({
        approvalKey: z.string(),
        decidedBy: z.string().nullable(),
        decidedAt: z.string(),
      }),
    ),
    approvalsBlockedReason: z.string().nullable(),
  }),
  approvalNames: z.record(z.string(), z.string()),
  userNames: z.record(z.string(), z.string()),
  withdrawableErpAttempt: z
    .object({ docRef: z.string().nullable() })
    .nullable(),
});

const DraftSalesOrderSchema = z
  .object({
    id: UUIDSchema,
    organizationId: UUIDSchema,
    customerId: UUIDSchema.nullable().optional(),
    suggestedCustomerId: UUIDSchema.nullable().optional(),
    purchaseOrderNumber: z.string().nullable().optional(),
    status: z.string(),
    orderDate: z.string().nullable().optional(),
    shipDate: z.string().nullable().optional(),
    currency: z.string().nullable().optional(),
    salesOrderId: UUIDSchema.nullable().optional(),
    lineItems: z.array(DraftLineItemSchema),
  })
  .strip();

const DraftLineItemPatchSchema = z.object({
  customerIdentifier: z.string().nullable().optional(),
  customerDescription: z.string().nullable().optional(),
  quantity: NumericSchema.nullable().optional(),
  unitPrice: NumericSchema.nullable().optional(),
  amount: NumericSchema.nullable().optional(),
  unit: z.string().nullable().optional(),
  warehouse: z.string().nullable().optional(),
  selectedProductVariantId: UUIDSchema.nullable().optional(),
  selectedCustomerProductId: UUIDSchema.nullable().optional(),
});

const DraftHeaderPatchSchema = z.object({
  // Direct draft-row columns.
  customerId: UUIDSchema.nullable().optional(),
  purchaseOrderNumber: z.string().nullable().optional(),
  orderDate: z.string().nullable().optional(),
  shipDate: z.string().nullable().optional(),
  currency: z.string().nullable().optional(),
  shipToLocationId: UUIDSchema.nullable().optional(),
  billToLocationId: UUIDSchema.nullable().optional(),
  // Resolver-driven header fields, stored as user overrides once in draft form
  // (computed only at ingest; edits never re-run their resolver — see v2's
  // updateSalesOrderDraftField). shippingMethod additionally re-derives freight
  // terms, which the resolver dependency handles.
  shippingMethod: z.string().nullable().optional(),
  freightTerms: z.string().nullable().optional(),
  freightCarrierInstructions: z.string().nullable().optional(),
  // Request date is resolver-computed (request_date); editing it stores a user
  // override (its resolver never re-runs in draft form), like the fields above.
  requestDate: z.string().nullable().optional(),
  // Order type is resolver-computed as an { id, name } object; edits store the
  // picked option verbatim as a user override (mirrors v2's
  // updateSalesOrderDraftField, which accepts the full object).
  orderType: z
    .object({ id: z.number(), name: z.string() })
    .nullable()
    .optional(),
  // Warehouse (ship-from org code) — resolver-computed string, user-overridable.
  warehouse: z.string().nullable().optional(),
  // Sales credit — the salesperson credited with the order (a Salespersons
  // CoreEnumOption id, or a raw ERP salesperson id). Resolver-computed from the
  // customer's primary salesperson, user-overridable.
  salesCredit: z.string().nullable().optional(),
  // Supplier (Supplier.id) and supplier site (site name) — resolver-computed
  // from the lines' sourcing signals, user-overridable. Changing the supplier
  // re-derives the site unless the site is itself overridden.
  supplier: z.string().nullable().optional(),
  supplierSite: z.string().nullable().optional(),
  // Payment / shipping terms as the reviewer reads them off the PO — stored as
  // user overrides; the approval facts and the terms-policy check honour them.
  paymentTerms: z.string().nullable().optional(),
  shippingTerms: z.string().nullable().optional(),
});

const SuccessSchema = z.object({ success: z.literal(true) });

/** Source document descriptor for the provenance viewer (signed URL + kind). */
const ProvenanceDocumentSchema = z.object({
  id: UUIDSchema,
  originalFileName: z.string(),
  mimeType: z.string().nullable(),
  url: z.string(),
  kind: z.enum(["pdf", "image", "xlsx", "emailBody"]),
});

/** Field-level source provenance for a draft, grouped for the review page. */
const DraftProvenanceResponseSchema = z.object({
  documents: z.array(ProvenanceDocumentSchema),
  headerFields: z.array(FieldProvenanceSchema),
  lineItems: z.array(
    z.object({
      lineItemId: UUIDSchema,
      lineNumber: z.number().int(),
      fields: z.array(FieldProvenanceSchema),
    }),
  ),
  unmappedBlocks: z.array(FieldProvenanceSchema),
  totalsReconciliation: z.object({
    extractedTotal: z.number().nullable(),
    computedLineItemsTotal: z.number(),
    matches: z.boolean(),
  }),
});

/** A single node in the workflow pipeline projection (review-page stepper). */
const WorkflowStateNodeSchema = z.object({
  id: z.string(),
  label: z.string(),
  status: z.enum(["done", "active", "wait"]),
  sub: z.string().nullable(),
  group: z.literal("par").nullable(),
  kind: z.literal("approval").nullable(),
  role: z.string().nullable(),
  assignee: z.string().nullable(),
  decision: z
    .enum(["approved", "rejected", "escalated", "timed_out", "pending"])
    .nullable(),
  /** Latest StepRun id for the step — the `submitWorkflowApproval` handle. */
  stepRunId: UUIDSchema.nullable(),
  /** The requesting user may decide this awaiting approval gate right now. */
  canDecide: z.boolean(),
});

// Keep in sync with `OrderLifecycleStage` in `@erp/shared-types`.
const LifecycleStageSchema = z.enum([
  "draft",
  "creating_discounts",
  "approving",
  "ready",
  "created",
  "under_cs_review",
  "rejected",
  "superseded",
]);
// Stages grow (`under_cs_review` joined the branches), and every response
// carries one. x-extensible-enum tells oasdiff the set is allowed to grow in
// responses; requests keep the closed enum.
JSON_SCHEMA_OUTPUT_REGISTRY.add(LifecycleStageSchema, {
  enum: undefined,
  "x-extensible-enum": LifecycleStageSchema.options,
} as { enum: undefined });

/**
 * Rolled-up workflow state for the lifecycle stepper + pipeline. `hasWorkflow`
 * is false for drafts with no run yet (the stepper still renders `stage`).
 */
const WorkflowStateSchema = z.object({
  stage: LifecycleStageSchema,
  hasWorkflow: z.boolean(),
  nodes: z.array(WorkflowStateNodeSchema),
  approvalsTotal: z.int().nonnegative(),
  approvalsDone: z.int().nonnegative(),
  rejected: z.boolean(),
  rejectionComment: z.string().nullable(),
  /** Display name of who rejected the order (gate decision or header reject). */
  rejectedBy: z.string().nullable(),
  /** ISO timestamp of the rejection, when known. */
  rejectedAt: z.string().nullable(),
  salesOrderId: UUIDSchema.nullable(),
  /** Last failed ERP submission attempt, until a retry succeeds. */
  submissionError: z.object({ message: z.string(), at: z.string() }).nullable(),
});

/** A single per-draft activity entry (spec §6 Activity rail). */
const ActivityEntrySchema = z.object({
  type: z.string(),
  at: z.string(),
  actorUserId: z.string().nullable(),
  actorName: z.string().nullable(),
  message: z.string().nullable(),
  lineItemId: z.string().nullable(),
  valuesChanged: z.record(z.string(), z.unknown()).nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  /** Present when the user made this change through Nami ("via Nami"). */
  via: AgentChannelSchema.nullable(),
});

/** A single extraction-check mismatch (spec §5) — common shape across detectors. */
const MismatchSchema = z.object({
  key: z.string(),
  detectionMethod: z.enum([
    "totals_reconciliation",
    "line_amount_mismatch",
    "line_discount_math",
    "line_no_price",
    "ship_to_unresolved",
    "terms_policy",
    "header_field_warning",
    "sibling_document_conflict",
    "coverage_sweep",
    "email_cross_reference",
    "catalog_validation",
  ]),
  status: z.enum(["open", "added", "pending", "dismissed"]),
  severity: z.enum(["warning", "info"]),
  title: z.string(),
  detail: z.string(),
  evidence: z.array(
    z.object({
      source: z.string(),
      label: z.string(),
      value: z.string(),
    }),
  ),
  resolvedAt: z.string().nullable(),
  resolvedByUserId: z.string().nullable(),
  resolutionReason: z.string().nullable(),
});

/**
 * Result of an ERP inventory-availability check for one line (mirrors
 * `InventoryValidationResult` in @erp/shared-types).
 */
const InventoryCheckResultSchema = z.object({
  status: z.string(),
  requestedQuantity: z.number(),
  availableQuantity: z.number().nullable(),
  onhandQuantity: z.number().nullable(),
  reservedQuantity: z.number().nullable(),
  organizationCode: z.string().nullable(),
  itemNumber: z.string().nullable(),
  errorMessage: z.string().optional(),
  itemStatus: z.string().optional(),
});

/** Source-email descriptor for the review page Emails tab. */
const SourceEmailThreadSchema = z.object({
  /** The draft's source type (e.g. "email", "edi", "direct_entry"). */
  source: z.string().nullable(),
  /** Graph conversation/thread id — feeds the email-thread viewer. */
  conversationId: z.string().nullable(),
  /** The source inbound email id (for highlighting in the thread). */
  sourceEmailId: UUIDSchema.nullable(),
  /** Sender of the source email (seeds the reply composer's To). */
  fromAddress: z.string().nullable(),
  /** Subject of the source email. */
  subject: z.string().nullable(),
});

export const draftSalesOrdersContracts = {
  get: oc
    .input(z.object({ id: UUIDSchema }))
    .output(z.object({ draftSalesOrder: DraftSalesOrderSchema }))
    .route({
      method: "GET",
      summary: "Get a draft sales order",
      description:
        "Returns one draft sales order by ID with its header fields, status, and all line items including matched products, prices, and inventory status.",
    }),
  // Workflow-state projection for the review-page lifecycle stepper + pipeline.
  getWorkflowState: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(WorkflowStateSchema)
    .route({
      method: "GET",
      summary: "Get draft order approval workflow state",
      description:
        "Returns the draft's lifecycle stage, approval pipeline steps with assignees and decisions, whether you can decide, rejection details, and any ERP submission error.",
    }),
  // Per-line discount validation for the v3 review page (reuses the relational
  // draft validator). Keyed by line-item id. Powers the per-line DiscountBadge
  // popover + the bulk create/approve flows.
  validateDiscounts: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(
      z.object({
        results: z.record(z.string(), DiscountValidationResultSchema),
      }),
    )
    .route({
      method: "GET",
      summary: "Validate discounts on a draft order",
      description:
        "Checks each line's applied discounts on a draft sales order and returns per-line validation results keyed by line item ID. Read-only.",
    }),
  // Extraction-check / mismatch detection (spec §5). A stateless second pass;
  // resolution state (dismissed / added / pending) persists per mismatch key.
  getMismatches: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(z.object({ mismatches: z.array(MismatchSchema) }))
    .route({
      method: "GET",
      summary: "List extraction mismatches on a draft order",
      description:
        "Detects issues between the source document and the draft (totals, line amounts, missing prices, unresolved ship-to, terms) and returns each with evidence and resolution status.",
    }),
  resolveMismatch: oc
    .input(
      z.object({
        draftSalesOrderId: UUIDSchema,
        key: z.string().min(1),
        status: z.enum(["open", "added", "pending", "dismissed"]),
        reason: z.string().max(2000).nullable().optional(),
      }),
    )
    .output(SuccessSchema)
    .route({
      method: "POST",
      summary: "Resolve an extraction mismatch on a draft",
      description:
        "Sets a draft mismatch's status (open, added, pending, dismissed) with an optional reason; the resolution is saved and logged to the draft's activity.",
    }),
  // Retry a failed ERP submission: re-emits DRAFT_SALES_ORDER_READY so the
  // approval chain re-runs (gates auto-pass from recorded decisions) and the
  // Submit step attempts the submission again.
  retryErpSubmission: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(SuccessSchema)
    .route({
      method: "POST",
      summary: "Retry failed ERP submission for a draft",
      description:
        "Restarts the draft's approval workflow after a failed ERP submission; already-recorded approvals pass automatically and the order is submitted to the ERP again.",
    }),
  // After an ERP rejection: pull the picked lines off the order (stamped as
  // `erp_rejected` exception items), withdraw a numberless SalesOrder if one
  // exists, and re-emit DRAFT_SALES_ORDER_READY so the order is submitted again.
  excludeLinesAndResubmit: oc
    .input(
      z.object({
        draftSalesOrderId: UUIDSchema,
        lineItemIds: z.array(UUIDSchema),
        /** Previously ERP-rejected lines to put back on the order. */
        restoreLineItemIds: z.array(UUIDSchema).optional(),
        reason: z.string().trim().min(1).max(500),
      }),
    )
    .output(SuccessSchema)
    .route({
      method: "POST",
      summary: "Exclude lines and resubmit a draft to the ERP",
      description:
        "Marks the picked lines as ERP-rejected exception items (and restores any previously rejected lines passed back), withdraws an ERP attempt that never received an order number, and restarts the approval workflow so the order is submitted again without them. With no line changes it is a plain resubmit of a numberless attempt.",
    }),
  // A revision draft inherits the reviewer's work on the previous revision:
  // header overrides, manual line prices and (when the lines still match)
  // the human approvals. Preview first; apply re-emits READY.
  previewRevisionCarryForward: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(RevisionCarryForwardPreviewSchema)
    .route({
      method: "GET",
      summary:
        "Preview what a revision would inherit from the previous revision",
      description:
        "Lists the previous revision's header overrides, manual line price edits and approved gates that apply to this revision draft, with the reasons anything is skipped, plus whether the previous revision's unnumbered ERP attempt can be withdrawn.",
    }),
  applyRevisionCarryForward: oc
    .input(
      z.object({
        draftSalesOrderId: UUIDSchema,
        /** Also withdraw the previous revision's unnumbered ERP attempt and mark it superseded. */
        withdrawOriginal: z.boolean().default(false),
      }),
    )
    .output(
      z.object({
        headerFieldsApplied: z.number().int(),
        linePricesApplied: z.number().int(),
        approvalsCarried: z.number().int(),
        originalWithdrawn: z.boolean(),
      }),
    )
    .route({
      method: "POST",
      summary:
        "Carry the previous revision's edits and approvals into this revision",
      description:
        "Applies the previewed header overrides and line prices, records the previous revision's approvals as carried forward so the gates pass automatically, optionally withdraws the previous revision's unnumbered ERP attempt and marks it superseded, then restarts this revision's approval workflow.",
    }),
  // Admin escape hatch: replay the approval-chain workflow for the draft
  // (facts recompute, gates route on recorded decisions, submit re-attempts).
  reinitiateWorkflow: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(SuccessSchema)
    .route({
      method: "POST",
      summary: "Restart the approval workflow for a draft",
      description:
        "Admin action that replays the draft's approval-chain workflow: recomputes facts, routes gates on recorded decisions, and re-attempts ERP submission.",
    }),
  // Per-draft activity timeline (spec §6) — the action-activity log, newest first.
  getActivity: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(z.object({ entries: z.array(ActivityEntrySchema) }))
    .route({
      method: "GET",
      summary: "Get draft order activity timeline",
      description:
        "Returns the draft sales order's activity log, newest first: who changed what, field diffs, line references, and whether the change came via the assistant.",
    }),
  updateLineItem: oc
    .input(
      z.object({
        draftSalesOrderId: UUIDSchema,
        lineItemId: UUIDSchema,
        patch: DraftLineItemPatchSchema,
      }),
    )
    .output(SuccessSchema)
    .route({
      method: "PATCH",
      summary: "Update a line item on a draft order",
      description:
        "Edits a draft line's quantity, price, unit, warehouse, description, or matched product. Product changes remove the line's discounts; affected approvals are re-evaluated.",
    }),
  // Match a line to a product variant (the review-page "Match" column). Beyond
  // updateLineItem, this also upserts the CustomerProduct mapping (customer
  // freeform learning, v2 parity) so future orders from the customer
  // auto-match, then writes selectedProductVariantId/-CustomerProductId through
  // the shared line-edit path (discount-stack removal, fact re-stamp, approval
  // invalidation). Learning is skipped when the draft has no customer.
  matchLineItem: oc
    .input(
      z.object({
        draftSalesOrderId: UUIDSchema,
        lineItemId: UUIDSchema,
        productVariantId: UUIDSchema,
      }),
    )
    .output(SuccessSchema)
    .route({
      method: "POST",
      summary: "Match a draft line to a product",
      description:
        "Sets a draft line's product and saves the customer's item-to-product mapping so future orders auto-match. Removes the line's discounts and re-evaluates affected approvals.",
    }),
  // Append a manually-added line item (product search + qty + unit price). The
  // backend stamps the next line number; list-price facts are (re)stamped by the
  // approval recheck that the add triggers.
  addLineItem: oc
    .input(
      z.object({
        draftSalesOrderId: UUIDSchema,
        selectedProductVariantId: UUIDSchema,
        quantity: NumericSchema,
        unitPrice: NumericSchema,
        unit: z.string().nullable().optional(),
        customerIdentifier: z.string().nullable().optional(),
        customerDescription: z.string().nullable().optional(),
      }),
    )
    .output(SuccessSchema)
    .route({
      method: "POST",
      summary: "Add a line item to a draft order",
      description:
        "Appends a product line with quantity and unit price to a draft sales order; assigns the next line number and re-evaluates affected approvals.",
    }),
  deleteLineItem: oc
    .input(
      z.object({
        draftSalesOrderId: UUIDSchema,
        lineItemId: UUIDSchema,
      }),
    )
    .output(SuccessSchema)
    .route({
      method: "DELETE",
      summary: "Delete a line item from a draft order",
      description:
        "Removes a line from a draft sales order, logs it to activity, and re-evaluates affected approvals since order totals change.",
    }),
  updateHeader: oc
    .input(
      z.object({
        draftSalesOrderId: UUIDSchema,
        patch: DraftHeaderPatchSchema,
      }),
    )
    .output(SuccessSchema)
    .route({
      method: "PATCH",
      summary: "Update draft order header fields",
      description:
        "Edits a draft's customer, PO number, dates, ship-to/bill-to, shipping method, freight, warehouse, sales credit, supplier, or terms; re-evaluates affected approvals.",
    }),
  // Field-level source provenance (Extend bounding boxes) for the review page:
  // source documents + per-header / per-line / unmapped citations + a totals
  // reconciliation. Read-only.
  getProvenance: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(DraftProvenanceResponseSchema)
    .route({
      method: "GET",
      summary: "Get source provenance for a draft order",
      description:
        "Returns the source documents and where each header and line field was read from, plus unmapped text and a totals reconciliation. Read-only.",
    }),
  // Source-email descriptor for the review page's Emails tab. Resolves the
  // draft (org-scoped) → its source InboundEmail, returning the conversationId
  // the existing email-thread viewer fetches the full thread with, plus the
  // sender + subject to seed a reply. conversationId is null for non-email
  // sources (EDI / direct entry) or emails without a Graph thread.
  getSourceEmailThread: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(SourceEmailThreadSchema)
    .route({
      method: "GET",
      summary: "Get source email for a draft order",
      description:
        "Returns the draft's source type and, for email orders, the email thread ID, sender, and subject used to view the thread or reply.",
    }),
  // Inventory-availability check for the draft's lines (v3 counterpart of the
  // legacy staged-PO validate-inventory endpoint). POST but read-mostly: it
  // queries the org's ERP and persists each line's inventoryStatus. Results
  // are keyed by productVariantId (and customerProductId where matched via one).
  validateInventory: oc
    .input(z.object({ draftSalesOrderId: UUIDSchema }))
    .output(
      z.object({ results: z.record(z.string(), InventoryCheckResultSchema) }),
    )
    .route({
      method: "POST",
      summary: "Check inventory for a draft order's lines",
      description:
        "Queries the ERP for available, on-hand, and reserved quantities for each draft line, saves each line's inventory status, and returns results by product.",
    }),
} as const;

export type DraftInventoryCheckResult = z.infer<
  typeof InventoryCheckResultSchema
>;
export type DraftSalesOrderDto = z.infer<typeof DraftSalesOrderSchema>;
export type DraftLineItemDto = z.infer<typeof DraftLineItemSchema>;
export type DraftSalesOrderWorkflowState = z.infer<typeof WorkflowStateSchema>;
export type DraftMismatch = z.infer<typeof MismatchSchema>;
export type DraftActivityEntry = z.infer<typeof ActivityEntrySchema>;
export type DraftSalesOrderWorkflowNode = z.infer<
  typeof WorkflowStateNodeSchema
>;
export type DraftLineItemPatch = z.infer<typeof DraftLineItemPatchSchema>;
export type DraftHeaderPatch = z.infer<typeof DraftHeaderPatchSchema>;
export type DraftProvenanceResponse = z.infer<
  typeof DraftProvenanceResponseSchema
>;
export type SourceEmailThread = z.infer<typeof SourceEmailThreadSchema>;
export type RevisionCarryForwardPreview = z.infer<
  typeof RevisionCarryForwardPreviewSchema
>;
